import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Localized, useLocalization } from "@fluent/react"
import {
    Alert, Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
    DialogTitle, Divider, FormControlLabel, IconButton, List, ListItem, ListItemButton, MenuItem,
    ListItemText, Paper, Skeleton, Tab, Tabs, TextField, Tooltip, Typography,
} from "@mui/material"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import AddIcon from "@mui/icons-material/Add"
import ApartmentIcon from "@mui/icons-material/Apartment"
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted"
import { APIError, unknownError } from "../../../app/appTypes"
import { CancelButton } from "../../../app/components/CancelButton"
import { LpField } from "../../../app/components/LpField"
import { SaveButton } from "../../../app/components/SaveButton"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { LoadingContainer } from "../../../app/LoadingContainer"
import { CompanyDepartmentDetails, CompanyDepartmentMember, loadCompanyDepartments } from "../../administration/companyDepartments/companyDepartmentsSlice"
import { loadMeetingMinutesAgenda, MeetingAgendaItemDefinitionDto } from "../../project/mettingMinutes/meetingMinutesSlice"
import { selectLoggedUser } from "../../user/userSlice"
import { UserNameOption, UserPicker } from "../../users/UserPicker"
import { selectAllUsers, selectUsers } from "../../users/usersSlice"
import {
    createOperationalDashboard, defaultOperationalDashboardOverviewSettings, getOperationalDashboardOverviewSettings,
    loadOperationalDashboardConfiguration,
    OperationalDashboardConfigurationDto, OperationalDashboardDto, OperationalDashboardOverviewSettings,
    SaveOperationalDashboardAreaDto, SaveOperationalDashboardDto, updateOperationalDashboard,
} from "../operationalDashboards"

type CreateEditOperationalDashboardProps = {
    onSuccessfullySaved: (dashboard: OperationalDashboardDto) => void
    onCancel: () => void
} & (
        | { mode: "create"; dashboard?: never }
        | { mode: "edit"; dashboard: OperationalDashboardDto }
    )

type AddSource = "new" | "department" | "existing"
const emptyResponsible = (): Pick<SaveOperationalDashboardAreaDto, "responsibleUserName" | "responsibleName" | "responsibleEmail"> => ({
    responsibleUserName: null, responsibleName: "", responsibleEmail: "",
})

type EditableArea = SaveOperationalDashboardAreaDto & {
    // UI-only state; excluded from the API request.
    clientKey: string
    source: AddSource
}

const EMPTY_MEMBERS: CompanyDepartmentMember[] = []

const createEmptyArea = (): SaveOperationalDashboardAreaDto => ({
    id: null,
    companyDepartmentId: null,
    structureNodeId: null,
    meetingAgendaItemSharedGuid: null,
    name: "",
    description: "",
    order: 0,
    isActive: true,
    responsibleUserName: null,
    responsibleName: "",
    responsibleEmail: "",
})

const toEditableArea = (area: OperationalDashboardConfigurationDto["areas"][number]): EditableArea => ({
    clientKey: `saved-${area.id}`,
    source: area.companyDepartmentId != null ? "department" : area.meetingAgendaItemSharedGuid ? "existing" : "new",
    id: area.id, companyDepartmentId: area.companyDepartmentId,
    structureNodeId: area.structureNodeId,
    meetingAgendaItemSharedGuid: area.meetingAgendaItemSharedGuid,
    name: area.name, description: area.description ?? "", order: area.order,
    isActive: true, responsibleUserName: area.responsibleUserName,
    responsibleName: area.responsibleName ?? "", responsibleEmail: area.responsibleEmail ?? "",
})

export const CreateEditOperationalDashboard = ({
    mode, dashboard, onSuccessfullySaved, onCancel,
}: CreateEditOperationalDashboardProps) => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()
    const label = (key: string, fallback: string) => {
        const id = `operational-dashboard-${key}`
        // getString reports missing messages even when a fallback is supplied.
        // Check first to avoid reporting them on every keystroke. Always use the
        // current bundles so translations loaded later or a locale change work.
        if (!l10n.getBundle(id)) return fallback
        return l10n.getString(id, null, fallback)
    }
    const loggedUser = useAppSelector(selectLoggedUser)
    const users = useAppSelector(selectAllUsers)
    const usersStatus = useAppSelector(selectUsers).items.status
    const [form, setForm] = useState<Omit<SaveOperationalDashboardDto, "isActive" | "areas">>({
        name: dashboard?.name ?? "",
        description: dashboard?.description ?? "",
        ownerName: dashboard?.ownerName ?? loggedUser,
        isPublic: dashboard?.isPublic ?? false,
        configJson: getOperationalDashboardOverviewSettings(dashboard?.configJson),
    })
    const [tab, setTab] = useState(0)
    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState<APIError | null>(null)
    const [configuration, setConfiguration] = useState<OperationalDashboardConfigurationDto | null>(null)
    const [configurationLoading, setConfigurationLoading] = useState(mode === "edit")
    const [configurationError, setConfigurationError] = useState<APIError | null>(null)
    const [departments, setDepartments] = useState<CompanyDepartmentDetails[]>([])
    const [departmentsLoading, setDepartmentsLoading] = useState(true)
    const [departmentsError, setDepartmentsError] = useState<APIError | null>(null)
    const [agenda, setAgenda] = useState<MeetingAgendaItemDefinitionDto[]>([])
    const [agendaLoading, setAgendaLoading] = useState(false)
    const [agendaError, setAgendaError] = useState<APIError | null>(null)
    const [areas, setAreas] = useState<EditableArea[]>([])
    const [selectedAreaKey, setSelectedAreaKey] = useState<string | null>(null)
    const nextAreaKey = useRef(1)

    useEffect(() => {
        let active = true
        dispatch(loadCompanyDepartments()).then(result => {
            if (!active) return
            if (loadCompanyDepartments.fulfilled.match(result)) setDepartments(result.payload)
            else setDepartmentsError(result.payload ?? unknownError())
            setDepartmentsLoading(false)
        })
        return () => { active = false }
    }, [dispatch])

    useEffect(() => {
        let active = true
        setError(null)
        setConfiguration(null)
        setConfigurationError(null)
        setAreas([])
        setSelectedAreaKey(null)
        setAgenda([])
        setAgendaError(null)
        setAgendaLoading(false)
        setTab(0)

        if (mode === "create") {
            setConfigurationLoading(false)
            setForm({ name: "", description: "", ownerName: loggedUser, isPublic: false,
                configJson: defaultOperationalDashboardOverviewSettings })
        } else {
            setConfigurationLoading(true)
            dispatch(loadOperationalDashboardConfiguration(dashboard.id)).then(result => {
                if (!active) return
                if (loadOperationalDashboardConfiguration.fulfilled.match(result)) {
                    const config = result.payload
                    setConfiguration(config)
                    setForm({
                        name: config.name, description: config.description,
                        ownerName: config.ownerName, isPublic: config.isPublic,
                        configJson: getOperationalDashboardOverviewSettings(config.configJson),
                    })
                    const loadedAreas = config.areas.map(toEditableArea)
                    setAreas(loadedAreas)
                    setSelectedAreaKey(loadedAreas[0]?.clientKey ?? null)
                } else setConfigurationError(result.payload ?? unknownError())
                setConfigurationLoading(false)
            })
        }
        return () => { active = false }
    }, [mode, dashboard?.id, dispatch, loggedUser])

    useEffect(() => {
        const meetingMinutesId = configuration?.meetingMinutesId
        setAgenda([])
        setAgendaError(null)
        if (!meetingMinutesId) {
            setAgendaLoading(false)
            return
        }
        let active = true
        setAgendaLoading(true)
        dispatch(loadMeetingMinutesAgenda(meetingMinutesId)).then(result => {
            if (!active) return
            if (loadMeetingMinutesAgenda.fulfilled.match(result)) setAgenda(result.payload)
            else setAgendaError(result.payload ?? unknownError())
            setAgendaLoading(false)
        })
        return () => { active = false }
    }, [configuration?.meetingMinutesId, dispatch])

    const selectedAreaIndex = areas.findIndex(area => area.clientKey === selectedAreaKey)
    const editingArea = areas[selectedAreaIndex] ?? null
    const isNewArea = editingArea !== null && editingArea.id == null
    const usersByName = useMemo(() => new Map(users.map(user => [user.userName, user] as const)), [users])
    const departmentsById = useMemo(() => new Map(departments.map(department => [department.id, department] as const)), [departments])
    const agendaByGuid = useMemo(() => new Map(agenda.map(item => [item.sharedGuid, item] as const)), [agenda])
    const savedAreasById = useMemo(() => new Map(configuration?.areas.map(area => [area.id, area] as const)), [configuration])
    const availableDepartments = useMemo(() => {
        const used = new Set(areas.filter(area => area.clientKey !== selectedAreaKey).map(area => area.companyDepartmentId))
        return departments.filter(department => department.isActive && !used.has(department.id))
    }, [departments, areas, selectedAreaKey])
    const availableAgenda = useMemo(() => {
        const used = new Set(areas.filter(area => area.clientKey !== selectedAreaKey).map(area => area.meetingAgendaItemSharedGuid))
        return agenda.filter(item => item.isActive && !used.has(item.sharedGuid))
    }, [agenda, areas, selectedAreaKey])
    const selectedDepartment = editingArea?.companyDepartmentId != null
        ? departmentsById.get(editingArea.companyDepartmentId) : undefined
    const selectedMembers = selectedDepartment?.members ?? EMPTY_MEMBERS
    const selectedAgenda = editingArea?.meetingAgendaItemSharedGuid
        ? agendaByGuid.get(editingArea.meetingAgendaItemSharedGuid) : undefined
    const agendaName = (area: SaveOperationalDashboardAreaDto) =>
        (area.meetingAgendaItemSharedGuid ? agendaByGuid.get(area.meetingAgendaItemSharedGuid)?.name : undefined) ||
        (area.id != null ? savedAreasById.get(area.id)?.meetingAgendaItemName : undefined)

    // This describes the current binding. After saving, the DTO does not tell us
    // whether a standalone agenda point was created here or selected earlier.
    const getAreaBase = (area: SaveOperationalDashboardAreaDto, source?: AddSource) => {
        if (source === "department" || area.companyDepartmentId != null) return {
            kind: "department",
            title: label("base-department", "Dział"),
            name: (area.companyDepartmentId != null ? departmentsById.get(area.companyDepartmentId)?.name : undefined) ||
                (area.companyDepartmentId != null ? label("department-name-unavailable", "Nazwa działu niedostępna")
                    : label("base-select-department", "Wybierz dział")),
        }
        if (area.structureNodeId != null) return {
            kind: "structure",
            title: label("base-structure", "Struktura"),
            name: label("base-structure-node", "Powiązany węzeł struktury"),
        }
        if (source === "existing" || area.meetingAgendaItemSharedGuid) return {
            kind: "agenda",
            title: label("base-agenda", "Punkt agendy"),
            name: agendaName(area) || (area.meetingAgendaItemSharedGuid
                ? label("agenda-name-unavailable", "Nazwa punktu niedostępna")
                : label("base-select-agenda", "Wybierz punkt agendy")),
        }
        if (source === "new" || area.id == null) return {
            kind: "new",
            title: label("base-new-agenda", "Nowy punkt agendy"),
            name: area.name.trim() || label("base-enter-name", "Podaj nazwę"),
        }
        return {
            kind: "none", title: label("base-unlinked", "Brak powiązania"),
            name: label("no-agenda-item", "Brak powiązanego punktu agendy")
        }
    }
    const editingBase = editingArea ? getAreaBase(editingArea) : null
    const editingTileNumber = selectedAreaIndex + 1
    const hasActiveAgenda = useMemo(() => agenda.some(item => item.isActive), [agenda])
    const emptyAgendaMessage = hasActiveAgenda
        ? label("agenda-all-used", "Wszystkie aktywne punkty tej agendy mają już kafelki, także te ukryte.")
        : label("agenda-no-active-points", "W agendzie tego dashboardu nie ma aktywnych punktów do wybrania.")
    const isAreaComplete = (area: EditableArea) => {
        if (!area.name.trim()) return false
        if (area.id != null || area.source === "new") return true
        if (area.source === "department") return !departmentsLoading && !departmentsError &&
            area.companyDepartmentId != null && !!departmentsById.get(area.companyDepartmentId)?.isActive &&
            !areas.some(other => other.clientKey !== area.clientKey && other.companyDepartmentId === area.companyDepartmentId)
        return !!configuration?.meetingMinutesId && !agendaLoading && !agendaError &&
            !!area.meetingAgendaItemSharedGuid && !!agendaByGuid.get(area.meetingAgendaItemSharedGuid)?.isActive &&
            !areas.some(other => other.clientKey !== area.clientKey && other.meetingAgendaItemSharedGuid === area.meetingAgendaItemSharedGuid)
    }
    const incompleteAreaNumbers = areas.flatMap((area, index) => isAreaComplete(area) ? [] : [index + 1])
    const invalidDescriptionLines = !Number.isSafeInteger(form.configJson.linesInConsent) ||
        form.configJson.linesInConsent < 1
    const configurationIncomplete = useMemo(() => mode === "edit" && configuration?.areas.some(area =>
        area.responsibleUserName === undefined), [mode, configuration])
    const canSave = !!form.name.trim() && !configurationLoading && !configurationError &&
        !configurationIncomplete && !invalidDescriptionLines && incompleteAreaNumbers.length === 0 &&
        usersStatus.type === "loaded" && usersByName.has(form.ownerName) &&
        (mode === "create" || (!!configuration && configuration.id === dashboard.id))
    const missingRequiredData = !form.name.trim() ||
        (usersStatus.type === "loaded" && !usersByName.has(form.ownerName)) ||
        areas.some(area => !area.name.trim() || (area.id === null &&
            ((area.source === "department" && area.companyDepartmentId === null) ||
                (area.source === "existing" && !area.meetingAgendaItemSharedGuid))))

    const addArea = () => {
        if (isSaving) return
        const area: EditableArea = {
            ...createEmptyArea(),
            clientKey: `new-${nextAreaKey.current++}`,
            source: "new",
            order: Math.max(0, ...areas.map(item => item.order)) + 1,
        }
        setAreas(previous => [...previous, area])
        setSelectedAreaKey(area.clientKey)
        setTab(1)
    }
    const selectAddSource = (source: AddSource) => {
        if (isSaving || !isNewArea || (source === "existing" && !configuration?.meetingMinutesId)) return
        setAreas(previous => previous.map(area =>
            area.clientKey === selectedAreaKey && area.source !== source
                ? { ...area, source, companyDepartmentId: null, structureNodeId: null, meetingAgendaItemSharedGuid: null }
                : area))
    }
    const changeTab = (value: number) => {
        if (!isSaving) setTab(value)
    }
    const updateOverviewSettings = (changes: Partial<OperationalDashboardOverviewSettings>) => {
        setForm(previous => ({
            ...previous,
            configJson: { ...previous.configJson, ...changes },
        }))
    }
    const editArea = useCallback((changes: Partial<EditableArea>) => {
        if (isSaving || selectedAreaKey === null) return
        setAreas(previous => previous.map(area =>
            area.clientKey === selectedAreaKey ? { ...area, ...changes } : area))
    }, [isSaving, selectedAreaKey])
    const removeArea = (index: number) => {
        if (isSaving || !areas[index]) return
        const nextAreas = areas.filter((_, position) => position !== index)
        setAreas(nextAreas)
        if (areas[index].clientKey === selectedAreaKey) {
            setSelectedAreaKey(nextAreas[Math.min(index, nextAreas.length - 1)]?.clientKey ?? null)
        }
    }

    const responsibleUserName = editingArea?.responsibleUserName ?? ""
    const departmentId = editingArea?.companyDepartmentId ?? null
    const existingResponsibleName = responsibleUserName ? editingArea?.responsibleName ?? "" : ""
    const existingResponsibleEmail = responsibleUserName ? editingArea?.responsibleEmail ?? "" : ""
    const handleResponsibleChange = useCallback((option: UserNameOption | null) => {
        if (!option?.userName) editArea(emptyResponsible())
        else editArea({ responsibleUserName: option.userName, responsibleName: option.name, responsibleEmail: option.email })
    }, [editArea])
    const handleManualResponsibleChange = (field: "responsibleName" | "responsibleEmail", value: string) => {
        if (!editingArea) return
        editArea({
            responsibleUserName: null,
            responsibleName: field === "responsibleName" ? value : responsibleUserName ? "" : editingArea.responsibleName,
            responsibleEmail: field === "responsibleEmail" ? value : responsibleUserName ? "" : editingArea.responsibleEmail,
        })
    }

    const handleSave = async () => {
        if (isSaving || !canSave) return
        const dto: SaveOperationalDashboardDto = {
            name: form.name.trim(), description: form.description.trim(), ownerName: form.ownerName.trim(),
            isPublic: form.isPublic, isActive: configuration?.isActive ?? true,
            configJson: form.configJson,
            areas: areas.map(({ clientKey, source, ...area }) => ({
                ...area, name: area.name.trim(), description: area.description.trim(), isActive: true,
            })),
        }
        setIsSaving(true)
        setError(null)
        const result = mode === "create"
            ? await dispatch(createOperationalDashboard(dto))
            : await dispatch(updateOperationalDashboard({ id: dashboard.id, dto }))
        if (createOperationalDashboard.fulfilled.match(result) || updateOperationalDashboard.fulfilled.match(result)) {
            setIsSaving(false)
            onSuccessfullySaved(result.payload)
        } else {
            setError(result.payload ?? unknownError())
            setIsSaving(false)
        }
    }

    const displayedError = error ?? configurationError ?? (usersStatus.type === "error" ? usersStatus.error : null)
    const renderError = (failure: APIError) => failure.kind === "http" && failure.problem ? (
        <Localized id={failure.problem.title}><span>{failure.problem.detail || failure.problem.title}</span></Localized>
    ) : failure.kind === "connection" ? (
        <Localized id="connection-issue">Problem z połączeniem</Localized>
    ) : failure.kind === "http" ? (
        `${l10n.getString("error", null, "Błąd")} (HTTP ${failure.status})`
    ) : <Localized id="error-unknown">Wystąpił nieoczekiwany problem</Localized>

    return (
        <Dialog open fullWidth maxWidth="lg"
            aria-labelledby="operational-dashboard-dialog-title">
            <DialogTitle id="operational-dashboard-dialog-title"
                sx={{
                    bgcolor: "grey.200", color: "text.primary", typography: "subtitle1",
                    fontWeight: 600, px: { xs: 2, sm: 3 }, py: 1.25
                }}>
                <Localized id={mode === "create" ? "operational-dashboard-create" : "operational-dashboard-edit"}>
                    <span>{mode === "create" ? "Dodaj dashboard operacyjny" : "Edytuj dashboard operacyjny"}</span>
                </Localized>
            </DialogTitle>
            <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {displayedError && <Alert severity="error" onClose={error ? () => setError(null) : undefined}>
                        {renderError(displayedError)}
                    </Alert>}
                    {configurationIncomplete && <Alert severity="error">
                        <Localized id="operational-dashboard-config-incomplete">
                            Nie można zapisać: konfiguracja obszarów nie zawiera loginu odpowiedzialnego użytkownika.
                        </Localized>
                    </Alert>}
                    {configurationLoading ? <LoadingContainer /> : mode === "create" || configuration ? (
                        <>
                            <Tabs value={tab} onChange={(_, value: number) => changeTab(value)}
                                textColor="inherit" aria-label={label("settings-tabs", "Ustawienia dashboardu")}
                                sx={{
                                    minHeight: 42, borderBottom: 1, borderColor: "divider",
                                    "& .MuiTabs-indicator": { bgcolor: "text.primary" },
                                    "& .MuiTab-root": { minHeight: 42, textTransform: "none" }
                                }}>
                                <Tab id="dashboard-general-tab" aria-controls="dashboard-general-panel"
                                    disabled={isSaving} label={l10n.getString("general", null, "_Ogólne")} />
                                <Tab id="dashboard-tiles-tab" aria-controls="dashboard-tiles-panel"
                                    disabled={isSaving} label={`${label("tiles-tab", "Kafelki")} (${areas.length})`} />
                                <Tab id="dashboard-overview-tab" aria-controls="dashboard-overview-panel"
                                    disabled={isSaving} label={l10n.getString("settings", null, "_Ustawienia")} />
                            </Tabs>
                            {tab === 0 ? (
                                <Box role="tabpanel" id="dashboard-general-panel" aria-labelledby="dashboard-general-tab"
                                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <TextField label={l10n.getString("name", null, "Nazwa")} value={form.name}
                                        onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                                        required fullWidth disabled={isSaving} />
                                    <TextField label={l10n.getString("description", null, "Opis")} value={form.description}
                                        onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                                        fullWidth multiline minRows={3} disabled={isSaving} />
                                    <UserPicker selectedUserName={form.ownerName || null}
                                        label={l10n.getString("owner", null, "_Właściciel")}
                                        disabled={isSaving} size="medium" readOnly />
                                    <FormControlLabel control={<Checkbox checked={form.isPublic}
                                        onChange={e => setForm(prev => ({ ...prev, isPublic: e.target.checked }))}
                                        disabled={isSaving} />} label={label("is-public", "Publiczny")} />
                                </Box>
                            ) : tab === 1 ? (
                                <Box role="tabpanel" id="dashboard-tiles-panel" aria-labelledby="dashboard-tiles-tab"
                                    sx={{
                                        display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "260px minmax(0, 1fr)" },
                                        gap: 2, alignItems: "start"
                                    }}>
                                    <Paper variant="outlined" sx={{ minWidth: 0, overflow: "hidden" }}>
                                        <Box sx={{ px: 1, py: 1, bgcolor: "action.hover", borderBottom: 1, borderColor: "divider" }}>
                                            <Typography variant="subtitle2" fontWeight={600}>
                                                {label("tiles-tab", "Kafelki")} ({areas.length})
                                            </Typography>
                                        </Box>
                                        {areas.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                                {label("no-tiles", "Nie dodano jeszcze kafelków.")}
                                            </Typography>
                                        ) : (
                                            <List disablePadding sx={{ p: 1 }}>
                                                {areas.map((area, index) => (
                                                    <ListItem key={area.clientKey} disablePadding>
                                                        <ListItemButton selected={selectedAreaIndex === index}
                                                            aria-label={`${label("tile", "Kafelek")} ${index + 1}: ${area.name}.${area.id == null ? ` ${label("tile-status-new", "Nowy")}.` : ""}`}
                                                            onClick={() => setSelectedAreaKey(area.clientKey)} disabled={isSaving}
                                                            sx={{
                                                                borderRadius: 1, gap: 0.75, px: 1, py: 0.75, minHeight: 40,
                                                                "&.Mui-selected": { bgcolor: "action.selected", color: "primary.main" },
                                                                "&.Mui-selected:hover": { bgcolor: "action.selected" }
                                                            }}>
                                                            <LpField value={index + 1} sx={{ flexShrink: 0, width: 24, height: 24 }} />
                                                            <Tooltip title={area.name || (area.id == null ? label("new-tile", "Nowy kafelek") : label("unnamed-tile", "Bez nazwy"))}>
                                                                <ListItemText sx={{ minWidth: 0, my: 0 }}
                                                                    primary={area.name || (area.id == null ? label("new-tile", "Nowy kafelek") : label("unnamed-tile", "Bez nazwy"))}
                                                                    primaryTypographyProps={{
                                                                        variant: "body2", noWrap: true,
                                                                        fontWeight: selectedAreaIndex === index ? 600 : 400
                                                                    }} />
                                                            </Tooltip>
                                                            {area.id == null && (
                                                                <Chip size="small" label={label("tile-status-new", "Nowy")} color="success"
                                                                    variant="outlined" sx={{
                                                                        ml: "auto", height: 20, fontSize: "0.65rem", flexShrink: 0,
                                                                        "& .MuiChip-label": { px: 0.75 }
                                                                    }} />
                                                            )}
                                                        </ListItemButton>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        )}
                                        <Box sx={{ p: 1.5, borderTop: 1, borderColor: "divider" }}>
                                            <Button variant="contained" color="success" disableElevation startIcon={<AddIcon />} fullWidth
                                                disabled={isSaving} onClick={addArea}
                                                sx={{ textTransform: "none", borderRadius: 1, minHeight: 38, fontWeight: 600 }}>
                                                {label("add-tile", "Dodaj kafelek")}
                                            </Button>
                                        </Box>
                                    </Paper>
                                    <Paper variant="outlined" sx={{ minWidth: 0, overflow: "hidden" }}>
                                        {editingArea ? (
                                            <>
                                                <Box sx={{
                                                    px: 2, py: 0.5, borderBottom: 1, borderColor: "divider",
                                                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1
                                                    , bgcolor: "grey.100"
                                                }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                                        <Typography variant="subtitle2" fontWeight={600}>
                                                            {label("tile-configuration-number", "Konfiguracja kafelka nr")}
                                                        </Typography>
                                                        <LpField value={editingTileNumber} sx={{ flexShrink: 0, width: 24, height: 24 }} />
                                                    </Box>
                                                    {selectedAreaIndex >= 0 && (
                                                        <Tooltip title={label("remove-tile", "Usuń kafelek z listy")}>
                                                            <IconButton color="error" size="small" disabled={isSaving}
                                                                aria-label={label("remove-tile", "Usuń kafelek z listy")}
                                                                onClick={() => removeArea(selectedAreaIndex)}>
                                                                <DeleteOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                                <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                                                    {isNewArea ? (
                                                        <>
                                                            <Box>
                                                                <Box role="group" aria-label={label("tile-base", "Baza kafelka")}
                                                                    sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 0.75 }}>
                                                                    {([
                                                                        ["new", "base-choice-new", "Nowy punkt agendy", AddIcon],
                                                                        ["department", "base-choice-department", "Z działu", ApartmentIcon],
                                                                        ["existing", "base-choice-existing", "Z agendy", FormatListBulletedIcon],
                                                                    ] as const).map(([source, titleKey, title, Icon]) => {
                                                                        const selected = editingArea.source === source
                                                                        const unavailable = source === "existing" && !configuration?.meetingMinutesId
                                                                        return <Tooltip key={source} title={unavailable
                                                                            ? label("agenda-requires-link", "Ten dashboard nie ma jeszcze przypisanej agendy.") : ""}>
                                                                            <Box component="span" sx={{ display: "flex", minWidth: 0 }}>
                                                                                <Button variant="outlined" fullWidth size="small" disabled={isSaving || unavailable}
                                                                                    startIcon={<Icon fontSize="small" />} onClick={() => selectAddSource(source)}
                                                                                    aria-pressed={selected}
                                                                                    sx={{
                                                                                        textTransform: "none", minHeight: 38, px: 1, py: 0.75,
                                                                                        fontWeight: selected ? 600 : 400, lineHeight: 1.3,
                                                                                        color: selected ? "primary.main" : "text.secondary",
                                                                                        borderColor: selected ? "primary.main" : "divider",
                                                                                        bgcolor: selected ? "action.selected" : "background.paper",
                                                                                        "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" }
                                                                                    }}>
                                                                                    {label(titleKey, title)}
                                                                                </Button>
                                                                            </Box>
                                                                        </Tooltip>
                                                                    })}
                                                                </Box>

                                                                {!configuration?.meetingMinutesId && (
                                                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                                                                        {label("base-existing-unavailable-short", "„Z agendy” wymaga agendy przypisanej do dashboardu.")}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            {editingArea.source === "department" && <>
                                                                {departmentsError && <Alert severity="error">{renderError(departmentsError)}</Alert>}
                                                                {departmentsLoading ? <Skeleton variant="rounded" height={40} /> : !departmentsError && (
                                                                    <Autocomplete size="small" options={availableDepartments} value={selectedDepartment ?? null}
                                                                        onChange={(_, department) => editArea({
                                                                            companyDepartmentId: department?.id ?? null,
                                                                            meetingAgendaItemSharedGuid: null,
                                                                            name: department?.name ?? "", description: department?.description ?? "",
                                                                            responsibleUserName: null, responsibleName: "", responsibleEmail: "",
                                                                        })}
                                                                        getOptionLabel={department => `${department.code} – ${department.name}`}
                                                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                                                        disabled={isSaving}
                                                                        renderInput={params => <TextField {...params} required label={label("select-department", "Wybierz dział")} />} />
                                                                )}
                                                            </>}
                                                            {editingArea.source === "existing" && <>
                                                                {agendaError && <Alert severity="error">{renderError(agendaError)}</Alert>}
                                                                {agendaLoading ? <Skeleton variant="rounded" height={40} /> : !agendaError && (
                                                                    <Autocomplete size="small" options={availableAgenda} value={selectedAgenda ?? null}
                                                                        noOptionsText={emptyAgendaMessage}
                                                                        onChange={(_, item) => editArea({
                                                                            companyDepartmentId: null, meetingAgendaItemSharedGuid: item?.sharedGuid ?? null,
                                                                            name: item?.name ?? "", description: item?.description ?? "",
                                                                            responsibleUserName: null, responsibleName: "", responsibleEmail: "",
                                                                        })}
                                                                        getOptionLabel={item => item.name}
                                                                        isOptionEqualToValue={(option, value) => option.sharedGuid === value.sharedGuid}
                                                                        disabled={isSaving}
                                                                        renderInput={params => <TextField {...params} required label={label("select-agenda-item", "Wybierz punkt agendy")} />} />
                                                                )}
                                                                {!agendaLoading && !agendaError && availableAgenda.length === 0 && (
                                                                    <Typography variant="body2" color="text.secondary" role="status">
                                                                        {emptyAgendaMessage}
                                                                    </Typography>
                                                                )}
                                                            </>}
                                                            <Divider />
                                                        </>
                                                    ) : null}
                                                    {!isNewArea && editingBase && (
                                                        <Box sx={{
                                                            display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap",
                                                            px: 1.25, py: 1, bgcolor: "action.hover", border: 1,
                                                            borderColor: "divider", borderRadius: 1,
                                                        }}>
                                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                                {label("tile-base", "Baza kafelka")}{":"}
                                                            </Typography>
                                                            {editingBase.kind === "department" || editingBase.kind === "structure"
                                                                ? <ApartmentIcon sx={{ fontSize: 17, color: "primary.main" }} />
                                                                : <FormatListBulletedIcon sx={{ fontSize: 17, color: "primary.main" }} />}
                                                            <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                                                                {editingBase.title}{": "}{editingBase.name}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    <TextField size="small" label={label("tile-name", "Nazwa kafelka")} value={editingArea.name}
                                                        onChange={event => editArea({ name: event.target.value })}
                                                        required fullWidth disabled={isSaving} />
                                                    <TextField size="small" label={l10n.getString("description", null, "Opis")} value={editingArea.description}
                                                        onChange={event => editArea({ description: event.target.value })}
                                                        fullWidth multiline minRows={2} maxRows={4} disabled={isSaving} />
                                                    {!isNewArea && editingArea.companyDepartmentId != null && departmentsError &&
                                                        <Alert severity="error">{renderError(departmentsError)}</Alert>}
                                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {l10n.getString("responsible-person", null, "_Osoba odpowiedzialna")}
                                                        </Typography>
                                                        <UserPicker key={editingArea.clientKey}
                                                            selectedUserName={responsibleUserName || null}
                                                            onChange={handleResponsibleChange}
                                                            members={departmentId != null ? selectedMembers : undefined}
                                                            fallbackName={existingResponsibleName}
                                                            fallbackEmail={existingResponsibleEmail}
                                                            disabled={isSaving || usersStatus.type !== "loaded" ||
                                                                (departmentId != null && (departmentsLoading || !!departmentsError))}
                                                            label={departmentId != null
                                                                ? label("responsible-department-user", "_Wybierz osobę z działu")
                                                                : label("responsible-system-user", "_Wybierz osobę z listy")}
                                                            noOptionsText={l10n.getString("no-matching-users", null, "_Brak pasujących użytkowników")} />
                                                        <Typography variant="body2" color="text.secondary">
                                                            {label("responsible-manual-entry", "_Lub wpisz ręcznie")}
                                                        </Typography>
                                                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
                                                            <TextField size="small" label={label("responsible-display-name", "_Imię i nazwisko lub nazwa")}
                                                                placeholder={label("responsible-display-name-example", "_Np. Jan Kowalski lub Zespół jakości")}
                                                                value={responsibleUserName ? "" : editingArea.responsibleName}
                                                                onChange={event => handleManualResponsibleChange("responsibleName", event.target.value)}
                                                                disabled={isSaving} />
                                                            <TextField size="small" label={l10n.getString("email-optional", null, "_E-mail (opcjonalnie)")}
                                                                value={responsibleUserName ? "" : editingArea.responsibleEmail}
                                                                onChange={event => handleManualResponsibleChange("responsibleEmail", event.target.value)}
                                                                disabled={isSaving} type="email" />
                                                        </Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ mt: -0.75 }}>
                                                            {label("responsible-manual-help", "_Te dane będą widoczne przy kafelku.")}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </>
                                        ) : <Box sx={{ p: 3 }}>
                                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                                                {label("select-tile-title", "Ustawienia kafelka")}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {label("select-tile-or-add", "Wybierz kafelek z listy lub dodaj nowy.")}
                                            </Typography>
                                        </Box>}
                                    </Paper>
                                </Box>
                            ) : (
                                <Box role="tabpanel" id="dashboard-overview-panel" aria-labelledby="dashboard-overview-tab"
                                    sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
                                    <FormControlLabel
                                        control={<Checkbox checked={form.configJson.showAgendaRating}
                                            onChange={event => updateOverviewSettings({ showAgendaRating: event.target.checked })}
                                            disabled={isSaving} />}
                                        label={label("overview-show-agenda-rating", "_Pokazuj ocenę agendy")} />
                                    <FormControlLabel
                                        control={<Checkbox checked={form.configJson.showTopicStatus}
                                            onChange={event => updateOverviewSettings({ showTopicStatus: event.target.checked })}
                                            disabled={isSaving} />}
                                        label={label("overview-show-topic-status", "_Pokazuj kropki priorytetu tematów")} />
                                    <FormControlLabel
                                        control={<Checkbox checked={form.configJson.showHeartbeat}
                                            onChange={event => updateOverviewSettings({ showHeartbeat: event.target.checked })}
                                            disabled={isSaving} />}
                                        label={label("overview-show-heartbeat", "_Pokazuj puls przy liczniku czasu spotkania")} />
                                    <TextField select size="small" disabled={isSaving}
                                        sx={{ width: 280, maxWidth: "100%" }}
                                        label={label("overview-default-expansion", "_Domyślny stan kafelków")}
                                        value={form.configJson.defaultAreaExpansion}
                                        onChange={event => updateOverviewSettings({
                                            defaultAreaExpansion: event.target.value as OperationalDashboardOverviewSettings["defaultAreaExpansion"],
                                        })}>
                                        <MenuItem value="collapsed">{label("overview-collapsed", "_Zwinięte")}</MenuItem>
                                        <MenuItem value="expanded">{label("overview-expanded", "_Rozwinięte")}</MenuItem>
                                    </TextField>
                                    <TextField size="small" type="number" disabled={isSaving}
                                        label={label("overview-description-lines", "_Liczba linii opisu w zwiniętym kafelku")}
                                        value={form.configJson.linesInConsent || ""}
                                        onChange={event => updateOverviewSettings({ linesInConsent: Number(event.target.value) })}
                                        inputProps={{ min: 1, step: 1 }}
                                        error={invalidDescriptionLines}
                                        helperText={invalidDescriptionLines
                                            ? label("overview-description-lines-error", "_Podaj dodatnią liczbę całkowitą.")
                                            : undefined} />
                                </Box>
                            )}
                        </>
                    ) : null}
                </Box>
            </DialogContent>
            <DialogActions>
                {(missingRequiredData || invalidDescriptionLines) && !configurationLoading && !configurationError && (
                    <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 200 } }}>
                        <Typography variant="caption" color="error.main" role="status">
                            {invalidDescriptionLines
                                ? label("overview-description-lines-error", "_Podaj dodatnią liczbę całkowitą.")
                                : label("required-data-help", "Uzupełnij dane wymagane")}
                        </Typography>
                    </Box>
                )}
                <SaveButton loading={isSaving} disabled={!canSave} onClick={handleSave} />
                <CancelButton loading={isSaving} disabled={isSaving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    )
}
